import { queryOptions, useQuery } from "@tanstack/react-query";
import { captureException } from "@sentry/react";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { apiClient, signApiRequest } from "./";
import {
  isFullCredentials,
  type CustomerCredentials,
} from "../ui/customerDataStore";

export type PoaCustomerStatus =
  "CUSTOMER" | "NOT_CUSTOMER" | "AWAITING_ANSWER" | "NO_ANSWER";

// Stamped by fmk-service when a POA stops being valid. Null on POAs invalidated before the stamp existed
export type PoaInactivatedReason =
  | "INACTIVITY_NO_ACTIVE_PRODUCT"
  | "NO_PENSION_DATA"
  | "EXPIRED"
  | "RECALLED"
  | "ADVISOR"
  | "DISABLED_EXTERNAL"
  | "TRANSFERRED"
  | "DELETED"
  | "REPLACED";

export interface PoaCompany {
  organisationIdCanonical: string;
  name: string;
  customerStatus: PoaCustomerStatus;
  responseDate?: string | null;
  hasInsurance?: boolean;
  logotype?: string | null;
}

export interface PoaStatusEntry {
  poaId: string;
  isValid: boolean;
  validFrom?: string | null;
  validTo?: string | null;
  signedDate?: string | null;
  inactivatedReason?: PoaInactivatedReason | null;
  inactivatedAt?: string | null;
  responsesByDate?: Record<string, PoaCompany[]>;
  pending?: PoaCompany[];
  companiesTotal?: number;
  companiesAnswered?: number;
  companiesWithInsurance?: number;
  // Companies that stayed silent past the backend's deadline. They are still listed in
  // pending, carrying customerStatus NO_ANSWER, so the app can stop waiting for them.
  companiesNoAnswer?: number;
  companiesPending?: number;
  lastAnswerAt?: string | null;
}

export interface PoaStatus {
  /** Ordered by the backend: valid first, then newest first. */
  poas: PoaStatusEntry[];
  /** Set while the customer may not sign a new POA: no pension data was found. */
  blockedUntil: string | null;
}

export const isPoaBlocked = (blockedUntil?: string | null): boolean =>
  !!blockedUntil && Date.parse(blockedUntil) > Date.now();

/** A POA date as the copy reads it, "12 juli 2026". */
export const formatPoaDate = (date: string): string =>
  dayjs(date).format("D MMMM YYYY");

const API_ENDPOINT = "/fmk/poa-status?fid=${identityId}";

interface RawPoaCompany {
  organisationId?: string;
  organisationIdCanonical: string;
  name?: string;
  customerStatus?: string;
  responseDate?: string | null;
  hasInsurance?: boolean;
  logotype?: string | null;
}

type RawPoaStatusEntry = Omit<PoaStatusEntry, "responsesByDate" | "pending"> & {
  responsesByDate?: Record<string, RawPoaCompany[]>;
  pending?: RawPoaCompany[];
};

const toCompany = (raw: RawPoaCompany): PoaCompany => ({
  organisationIdCanonical: raw.organisationIdCanonical,
  name: raw.name ?? "",
  // Response nodes written before customerStatus existed have none. Unknown is
  // not an answer, so it counts as still waiting rather than as a decided no.
  customerStatus:
    (raw.customerStatus as PoaCustomerStatus) ?? "AWAITING_ANSWER",
  responseDate: raw.responseDate ?? null,
  hasInsurance: raw.hasInsurance,
  logotype: raw.logotype ?? null,
});

const toEntry = (raw: RawPoaStatusEntry): PoaStatusEntry => ({
  ...raw,
  responsesByDate: Object.fromEntries(
    Object.entries(raw.responsesByDate ?? {}).map(([day, companies]) => [
      day,
      companies.map(toCompany),
    ]),
  ),
  pending: (raw.pending ?? []).map(toCompany),
});

export const getPoaStatus = async ({
  credentials,
}: {
  credentials: CustomerCredentials;
}): Promise<PoaStatus> => {
  const url = `${import.meta.env.VITE_API_URL}/fmk/poa-status?fid=${credentials.identityId}`;

  try {
    const signedRequest = await signApiRequest("GET", url, credentials);
    const response = await apiClient(signedRequest);
    // The service wraps the list in a `poas` envelope.
    return {
      poas: (response.data?.poas ?? []).map(toEntry),
      blockedUntil: response.data?.blockedUntil ?? null,
    };
  } catch (error) {
    // fmk answers 404 until the signed POA is readable in Neptune, which is a normal
    // moment on the happy path right after signing, not an error worth reporting. The
    // poll keeps running and picks the POA up as soon as it lands.
    if (isAxiosError(error) && error.response?.status === 404) {
      return { poas: [], blockedUntil: null };
    }

    captureException(error, {
      tags: {
        operation: "get-poa-status",
        errorType: (error as Error).name || "UnknownError",
      },
      extra: {
        apiEndpoint: API_ENDPOINT,
      },
      level: "error",
    });
    throw error;
  }
};

export const getPoaStatusQueryOptions = (credentials: CustomerCredentials) =>
  queryOptions({
    queryKey: ["get-poa-status", credentials.identityId],
    queryFn: () => getPoaStatus({ credentials }),
    retry: 1,
  });

export const useGetPoaStatus = ({
  credentials,
  enabled = true,
  isCollecting = false,
}: {
  credentials: CustomerCredentials;
  enabled?: boolean;
  /** Companies are still expected to answer, so keep polling. */
  isCollecting?: boolean;
}) =>
  useQuery({
    ...getPoaStatusQueryOptions(credentials),
    enabled: enabled && isFullCredentials(credentials),
    // A failed poll with data already on screen is not worth an error screen.
    throwOnError: (_error, query) => query.state.data === undefined,
    refetchInterval: isCollecting ? 30_000 : false,
  });
